import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, type TooltipProps, XAxis, YAxis } from 'recharts';
import { AXIS_TICK, CHART_SERIES_COLORS, GRID_STROKE, TOOLTIP_CONTENT_STYLE } from '@/components/charts/recharts-theme';
import { uniqueSorted } from '@/utils/array';
import { formatCompactUsd, formatUsd } from '@/utils/format';
import { depthWithin, type InstrumentDepth, requirementWithin } from './market-depth';

const CHART_HEIGHT = 220;
const CHART_MARGIN = { top: 12, right: 16, bottom: 4, left: 0 };
const X_OVERSHOOT = 1.15;
const NBBO_KEY = ':nbbo';
const COMPARE_KEY = 'compare';
const COMPARE_COLOR = 'hsl(var(--muted-foreground))';

type Point = { x: number } & Record<string, number>;

/** One colour per market maker, the same on every market's chart: assigned by name across all of them. */
export const marketMakerColors = (marketMakers: { pid: string; name: string }[]) =>
  new Map(
    [...new Map(marketMakers.map((marketMaker) => [marketMaker.pid, marketMaker])).values()]
      .sort((left, right) => left.name.localeCompare(right.name))
      .map((marketMaker, index) => [marketMaker.pid, CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length]]),
  );

const FALLBACK_COLOR = CHART_SERIES_COLORS[0];

/** Width in bps on x, cumulative USD on y: each market maker stacked on the others, NBBO buckets hatched. */
const toPoints = (depth: InstrumentDepth, compare: InstrumentDepth | undefined, xMax: number): Point[] => {
  const xs = uniqueSorted([0, ...depth.levels.map((level) => level.bps), ...(compare?.levels.map((level) => level.bps) ?? []), xMax], (left, right) => left - right);
  return xs.map((x) => {
    const point: Point = { x };
    for (const marketMaker of depth.marketMakers) {
      const bucket = requirementWithin(marketMaker.buckets, x);
      point[marketMaker.pid] = bucket && !bucket.nbbo ? bucket.kpi : 0;
      point[`${marketMaker.pid}${NBBO_KEY}`] = bucket?.nbbo ? bucket.kpi : 0;
    }
    if (compare) point[COMPARE_KEY] = depthWithin(compare, x).total;
    return point;
  });
};

interface DepthTooltipProps extends TooltipProps<number, string> {
  depth: InstrumentDepth;
  colors: Map<string, string>;
  compareLabel?: string;
}

const DepthTooltip = ({ active, payload, depth, colors, compareLabel }: DepthTooltipProps) => {
  const point = payload?.[0]?.payload as Point | undefined;
  if (!active || !point) return null;
  const { total, nbboTotal } = depthWithin(depth, point.x);
  return (
    <div className="flex flex-col gap-1 px-3 py-2 text-xs" style={TOOLTIP_CONTENT_STYLE}>
      <span className="font-medium">Within {point.x} bps of mid</span>
      <span className="tabular-nums">
        Total {formatUsd(total, 0)}
        {nbboTotal ? ` (${formatUsd(nbboTotal, 0)} from NBBO buckets)` : ''}
      </span>
      {depth.marketMakers.map((marketMaker) => {
        const value = (point[marketMaker.pid] ?? 0) + (point[`${marketMaker.pid}${NBBO_KEY}`] ?? 0);
        return value ? (
          <span className="inline-flex items-center gap-1.5 tabular-nums" key={marketMaker.pid}>
            <span aria-hidden className="size-2 rounded-sm" style={{ backgroundColor: colors.get(marketMaker.pid) ?? FALLBACK_COLOR }} />
            {marketMaker.name}: {formatUsd(value, 0)}
            {point[`${marketMaker.pid}${NBBO_KEY}`] ? ' + ½ NBBO' : ''}
          </span>
        ) : null;
      })}
      {compareLabel && point[COMPARE_KEY] !== undefined ? (
        <span className="text-muted-foreground tabular-nums">
          {compareLabel}: {formatUsd(point[COMPARE_KEY], 0)}
        </span>
      ) : null}
    </div>
  );
};
DepthTooltip.displayName = 'DepthTooltip';

interface DepthChartProps {
  depth: InstrumentDepth;
  colors: Map<string, string>;
  /** The same market on another day, drawn as a dashed total behind */
  compare?: InstrumentDepth;
  compareLabel?: string;
}

export const DepthChart = ({ depth, colors, compare, compareLabel }: DepthChartProps) => {
  const colorOf = (pid: string) => colors.get(pid) ?? FALLBACK_COLOR;
  const patternId = (pid: string) => `nbbo-${depth.instrument}-${pid}`;
  const widest = Math.max(...depth.levels.map((level) => level.bps), ...(compare?.levels.map((level) => level.bps) ?? []), 1);
  const xMax = Math.ceil(widest * X_OVERSHOOT);
  const points = toPoints(depth, compare, xMax);
  // A tick on each bucket width puts the axis labels where the steps are.
  const xTicks = uniqueSorted([0, ...depth.levels.map((level) => level.bps), xMax], (left, right) => left - right);
  const summary = `Cumulative liquidity asked for on ${depth.instrument}: ${depth.levels.map((level) => `${formatUsd(level.total, 0)} within ${level.bps} bps`).join(', ')}`;
  return (
    <div aria-label={summary} role="img">
      <ResponsiveContainer height={CHART_HEIGHT} width="100%">
        <ComposedChart data={points} margin={CHART_MARGIN}>
          <defs>
            {depth.marketMakers.map((marketMaker) => (
              <pattern height="6" id={patternId(marketMaker.pid)} key={marketMaker.pid} patternTransform="rotate(45)" patternUnits="userSpaceOnUse" width="6">
                <rect fill={colorOf(marketMaker.pid)} fillOpacity={0.15} height="6" width="6" />
                <line stroke={colorOf(marketMaker.pid)} strokeWidth="3" x1="0" x2="0" y1="0" y2="6" />
              </pattern>
            ))}
          </defs>
          <CartesianGrid stroke={GRID_STROKE} vertical={false} />
          <XAxis dataKey="x" domain={[0, xMax]} tick={AXIS_TICK} tickFormatter={(value: number) => `${value} bps`} ticks={xTicks} type="number" />
          <YAxis domain={[0, 'auto']} tick={AXIS_TICK} tickFormatter={formatCompactUsd} type="number" width={56} />
          <Tooltip content={<DepthTooltip colors={colors} compareLabel={compareLabel} depth={depth} />} isAnimationActive={false} />
          {depth.marketMakers.flatMap((marketMaker) => [
            <Area
              dataKey={marketMaker.pid}
              fill={colorOf(marketMaker.pid)}
              fillOpacity={0.55}
              isAnimationActive={false}
              key={marketMaker.pid}
              name={marketMaker.name}
              stackId="depth"
              stroke={colorOf(marketMaker.pid)}
              type="stepAfter"
            />,
            <Area
              dataKey={`${marketMaker.pid}${NBBO_KEY}`}
              fill={`url(#${patternId(marketMaker.pid)})`}
              isAnimationActive={false}
              key={`${marketMaker.pid}${NBBO_KEY}`}
              name={`${marketMaker.name} (NBBO)`}
              stackId="depth"
              stroke={colorOf(marketMaker.pid)}
              strokeDasharray="4 2"
              type="stepAfter"
            />,
          ])}
          {compare ? (
            <Line dataKey={COMPARE_KEY} dot={false} isAnimationActive={false} name={compareLabel} stroke={COMPARE_COLOR} strokeDasharray="5 4" strokeWidth={2} type="stepAfter" />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};
DepthChart.displayName = 'DepthChart';
